import React, { JSX, useState } from 'react';
import { FiEdit, FiArrowUp, FiArrowDown, FiSearch, FiPlus, FiX } from 'react-icons/fi';
import { HiOutlineDocumentText } from 'react-icons/hi';
import formatDate from '../../utils/format-date';
import Breadcrumb from '../../utils/breadcrumbs';
import OffCanvasForm from './offcanvas-form';

type PageStatus = 'active' | 'draft' | 'inactive';
type PageType = 'privacy' | 'terms' | 'cookie' | 'refund' | 'about' | 'faq' | 'custom';
type StatusFilter = 'all' | PageStatus;

interface IPage {
    _id?: string;
    title: string;
    slug: string;
    content: string;
    status: PageStatus;
    page_type: PageType;
    created_at?: string;
    updated_at?: string;
}

// Static data for demonstration
const pages: IPage[] = [
    {
        _id: '1',
        title: 'Terms and Conditions',
        slug: 'terms-and-conditions',
        content: 'Terms and conditions content...',
        status: 'active',
        page_type: 'terms',
        updated_at: '2024-01-15T10:30:00Z'
    },
    {
        _id: '2',
        title: 'Privacy Policy',
        slug: 'privacy-policy',
        content: 'Privacy policy content...',
        status: 'draft',
        page_type: 'privacy',
        updated_at: '2024-01-14T15:45:00Z'
    }
];

const STATUS_COLORS: Record<PageStatus, string> = {
    active: 'bg-green-100 text-green-800',
    draft: 'bg-yellow-100 text-yellow-800',
    inactive: 'bg-gray-100 text-gray-800'
};

const PAGE_TYPE_ICONS: Record<PageType, JSX.Element> = {
    privacy: <HiOutlineDocumentText className="w-5 h-5" />,
    terms: <HiOutlineDocumentText className="w-5 h-5" />,
    cookie: <HiOutlineDocumentText className="w-5 h-5" />,
    refund: <HiOutlineDocumentText className="w-5 h-5" />,
    about: <HiOutlineDocumentText className="w-5 h-5" />,
    faq: <HiOutlineDocumentText className="w-5 h-5" />,
    custom: <HiOutlineDocumentText className="w-5 h-5" />
};

const SitePages = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
    const [isOffCanvasOpen, setIsOffCanvasOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState<IPage | null>(null);

    const handleEditPage = (page: IPage) => {
        setCurrentPage(page);
        setIsOffCanvasOpen(true);
    };

    const handleAddPage = () => {
        setCurrentPage(null);
        setIsOffCanvasOpen(true);
    };

    const handleSubmit = (formData: IPage) => {
        console.log('Form submitted:', formData);
        // Here you would typically make an API call to save/update the page
        setIsOffCanvasOpen(false);
    };

    const handleCancel = () => {
        setIsOffCanvasOpen(false);
    };

    const filteredPages = pages.filter(page => {
        const matchesSearch = page.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            page.slug.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === 'all' || page.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    return (
        <div className="w-full min-h-screen bg-gradient-to-br from-white to-blue-50 px-2 py-6 md:p-6">
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 flex items-center">
                    <span className="bg-gray-900 w-1 h-6 mr-3 rounded"></span>
                    Pages
                </h2>
                <Breadcrumb />
            </div>

            <div className="w-full overflow-hidden bg-white rounded-lg shadow border">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 p-4">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <FiSearch className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                                type="text"
                                className="focus:ring-primary-500 py-2 border focus:border-primary-500 block w-full pl-10 border-gray-300 rounded-md"
                                placeholder="Search pages..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <div className="inline-flex rounded-md shadow-sm">
                            <select
                                className="focus:ring-primary-500 focus:border-primary-500 relative inline-flex items-center px-4 py-2 border border-gray-300 font-medium rounded-md bg-white text-gray-700 hover:bg-gray-50"
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                            >
                                <option value="all">All</option>
                                <option value="active">Active</option>
                                <option value="draft">Draft</option>
                                <option value="inactive">Inactive</option>
                            </select>
                        </div>
                    </div>
                    {/* Add new page button */}
                    {/* <button
                        onClick={handleAddPage}
                        className="inline-flex items-center px-4 py-2 border border-transparent font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-600"
                    >
                        <FiPlus className="mr-2 h-4 w-4" />
                        Add New Page
                    </button> */}
                </div>

                <div className="bg-white shadow overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-full divide-y divide-gray-200 border border-gray-200">
                            <thead className="bg-gray-50 sticky top-0">
                                <tr>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                        Title
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                        Slug
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                        Type
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                        Status
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                        Last Updated
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredPages.length > 0 ? (
                                    filteredPages.map((page) => (
                                        <tr key={page._id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="font-medium text-gray-900 truncate max-w-xs">{page.title}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-500 truncate max-w-xs">{page.slug}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <span className="mr-2">
                                                        {PAGE_TYPE_ICONS[page.page_type]}
                                                    </span>
                                                    <span className="text-sm text-gray-500 capitalize">
                                                        {page.page_type.replace('-', ' ')}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <span
                                                        className={`px-2 py-1 capitalize inline-flex text-xs leading-5 font-semibold rounded-full ${STATUS_COLORS[page.status]}`}
                                                    >
                                                        {page.status}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                                                {formatDate(page.updated_at as string)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right font-medium">
                                                <div className="flex justify-center space-x-4">
                                                    <button
                                                        className="text-primary-600 hover:text-primary-700 focus:outline-none"
                                                        title="Edit Page"
                                                        onClick={() => handleEditPage(page)}
                                                    >
                                                        <FiEdit className="h-5 w-5" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                                            No pages found matching your criteria
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Offcanvas Form */}
            <OffCanvasForm
                show={isOffCanvasOpen}
                onClose={handleCancel}
                page={currentPage || undefined}
                onSubmit={handleSubmit}
            />
        </div>
    );
};

export default SitePages;